export type RunCampeonSyncCollectibleType =
  | "first_boost_core"
  | "energy_trail"
  | "champion_shard"
  | "pressure_core"
  | "champion_core"
  | "champion_burst"
  | "spirit_energy"
  | "pack_echo_core"
  | "final_champion_core"
  | "final_echo";

export type RunCampeonSyncPlacement =
  | "center_safe"
  | "safe_slight_offset"
  | "soft_center_trail"
  | "soft_curve_trail"
  | "center_trail"
  | "safe_followup";

export type RunCampeonSyncIntensity = "medium" | "medium_high" | "high" | "max";

export interface RunCampeonSongSyncEvent {
  id: string;
  pickupCounterTime: number;
  type: RunCampeonSyncCollectibleType;
  intensity: RunCampeonSyncIntensity;
  placement: RunCampeonSyncPlacement;
  spawnLeadTime?: number;
  count: number;
  narrative: string;
  predicted?: boolean;
}

export const RUN_CAMPEON_SONG_SYNC_CONFIG = {
  enabled: true,
  gameDurationSeconds: 120,
  bpm: 130,

  timing: {
    beatDuration: 60 / 130,
    barDuration: (60 / 130) * 4,
    fourBarDuration: (60 / 130) * 16,
    defaultSpawnLeadTime: 2.2,
    pickupToleranceSeconds: 0.15,
    lateSkipSeconds: 0.65,
    trailItemSpacingSeconds: 0.12
  },

  rules: {
    useCounterTimeAsAuthoritative: true,
    useAudioTimeForClock: true,
    neverBlockGameplayOnSyncEvents: true,
    reserveSafePathForMajorPickups: true,
    avoidObstacleOverlap: true,
    avoidStrongScreenFlashes: true
  },

  debugSongSync: {
    enabled: false,
    showNextEvent: true,
    showPickupDelta: true
  },

  collectibles: {
    first_boost_core: { scoreValue: 6, energyRestoreMultiplier: 1.2, visualScale: 1.08, magnetRadius: 68, tint: 0xdfff2f },
    energy_trail: { scoreValue: 1, energyRestoreMultiplier: 0.34, visualScale: 0.66, magnetRadius: 44, tint: 0xcaff2a },
    champion_shard: { scoreValue: 3, energyRestoreMultiplier: 0.74, visualScale: 0.92, magnetRadius: 54, tint: 0xdfff2f },
    pressure_core: { scoreValue: 5, energyRestoreMultiplier: 0.9, visualScale: 1.02, magnetRadius: 58, tint: 0x82d9a1 },
    champion_core: { scoreValue: 8, energyRestoreMultiplier: 1.18, visualScale: 1.18, magnetRadius: 72, tint: 0xdfff2f },
    champion_burst: { scoreValue: 8, energyRestoreMultiplier: 1.12, visualScale: 1.14, magnetRadius: 72, tint: 0xf0ff99 },
    spirit_energy: { scoreValue: 2, energyRestoreMultiplier: 0.64, visualScale: 0.88, magnetRadius: 58, tint: 0x82d9a1 },
    pack_echo_core: { scoreValue: 10, energyRestoreMultiplier: 1.45, visualScale: 1.26, magnetRadius: 72, tint: 0xf0ff99 },
    final_champion_core: { scoreValue: 10, energyRestoreMultiplier: 1.35, visualScale: 1.24, magnetRadius: 72, tint: 0xf0ff99 },
    final_echo: { scoreValue: 12, energyRestoreMultiplier: 1.45, visualScale: 1.28, magnetRadius: 72, tint: 0xf0ff99 }
  } satisfies Record<
    RunCampeonSyncCollectibleType,
    { scoreValue: number; energyRestoreMultiplier: number; visualScale: number; magnetRadius: number; tint: number }
  >,

  events: [
    {
      id: "pickup_001_first_mirame_boost",
      pickupCounterTime: 112,
      type: "first_boost_core",
      intensity: "high",
      placement: "center_safe",
      spawnLeadTime: 2.2,
      count: 1,
      narrative: "first_call"
    },
    {
      id: "pickup_002_first_beat_response",
      pickupCounterTime: 105.3,
      type: "energy_trail",
      intensity: "medium",
      placement: "soft_center_trail",
      spawnLeadTime: 3.7,
      count: 4,
      narrative: "rhythm_lock"
    },
    {
      id: "pickup_003_phrase_continuation",
      pickupCounterTime: 97.3,
      type: "champion_shard",
      intensity: "medium",
      placement: "safe_slight_offset",
      spawnLeadTime: 3.7,
      count: 1,
      narrative: "inner_energy"
    },
    {
      id: "pickup_004_strong_entry",
      pickupCounterTime: 90,
      type: "champion_shard",
      intensity: "high",
      placement: "center_safe",
      spawnLeadTime: 2.2,
      count: 1,
      narrative: "pressure_rise"
    },
    {
      id: "pickup_005_transition_energy",
      pickupCounterTime: 84,
      type: "energy_trail",
      intensity: "medium",
      placement: "soft_curve_trail",
      spawnLeadTime: 2.2,
      count: 5,
      narrative: "movement_flow"
    },
    {
      id: "pickup_006_pressure_rise",
      pickupCounterTime: 75,
      type: "pressure_core",
      intensity: "high",
      placement: "center_safe",
      spawnLeadTime: 2.2,
      count: 1,
      narrative: "spirit_pressure"
    },
    {
      id: "pickup_007_main_explosion",
      pickupCounterTime: 68,
      type: "champion_core",
      intensity: "max",
      placement: "center_safe",
      spawnLeadTime: 2.4,
      count: 1,
      narrative: "main_champion_explosion"
    },
    {
      id: "pickup_008_double_burst",
      pickupCounterTime: 65,
      type: "champion_burst",
      intensity: "max",
      placement: "safe_followup",
      spawnLeadTime: 2.1,
      count: 1,
      narrative: "second_explosion_hit"
    },
    {
      id: "pickup_009_section_close",
      pickupCounterTime: 60,
      type: "energy_trail",
      intensity: "high",
      placement: "center_trail",
      spawnLeadTime: 2.2,
      count: 5,
      narrative: "section_reward"
    },
    {
      id: "pickup_010_second_big_explosion",
      pickupCounterTime: 52,
      type: "champion_core",
      intensity: "max",
      placement: "center_safe",
      spawnLeadTime: 2.4,
      count: 1,
      narrative: "second_champion_explosion"
    },
    {
      id: "pickup_011_response",
      pickupCounterTime: 45,
      type: "champion_shard",
      intensity: "medium_high",
      placement: "safe_slight_offset",
      spawnLeadTime: 2.2,
      count: 1,
      narrative: "response_energy"
    },
    {
      id: "pickup_012_new_push",
      pickupCounterTime: 38,
      type: "champion_core",
      intensity: "high",
      placement: "center_safe",
      spawnLeadTime: 2.3,
      count: 1,
      narrative: "renewed_push"
    }
  ] satisfies RunCampeonSongSyncEvent[],

  predictedEventsEnabled: false,
  predictedEvents: [
    { id: "predicted_001_counter_31", pickupCounterTime: 31, type: "energy_trail", intensity: "medium", placement: "center_trail", count: 4, narrative: "predicted_phrase", predicted: true },
    { id: "predicted_002_counter_23", pickupCounterTime: 23, type: "spirit_energy", intensity: "medium", placement: "center_safe", count: 1, narrative: "predicted_spirit", predicted: true },
    { id: "predicted_003_counter_16", pickupCounterTime: 16, type: "pack_echo_core", intensity: "high", placement: "center_safe", count: 1, narrative: "predicted_pack", predicted: true },
    { id: "predicted_004_counter_9", pickupCounterTime: 9, type: "final_champion_core", intensity: "max", placement: "center_safe", count: 1, narrative: "predicted_final_champion", predicted: true },
    { id: "predicted_005_counter_1", pickupCounterTime: 1, type: "final_echo", intensity: "high", placement: "center_safe", count: 1, narrative: "predicted_final_echo", predicted: true }
  ] satisfies RunCampeonSongSyncEvent[]
} as const;
